import GSObjectArray from './GSObjectArray';
import GSOUUIDArray from './GSOUUIDArray';
import Angle from './Math/Angle';

// The gamestudio base object class for things in the game.
export default class GSObject {
    // Make sure to call super(parent) on derived classes.
    constructor(parent) {
        this._gsoParent = parent;
        this._gsoChildren = [];
        if (this._gsoParent) {
            this._gsoParent._gsoChildren.push(this);
            this._inPlayMode = this._gsoParent._inPlayMode;
        }

        this._hasVisuals = false;
        this._hasBody = false;
        this._inPlayMode = false;
        this._enabled = true;
        this._requestedRecreateVisuals = false;
        this._respondsToUI = false;
        this._absorbClick = true;
    }

    _gsoCreateVisuals() {
        if (!this._enabled) return;
        if (!this._hasVisuals) {
            this._hasVisuals = true;
            this.onCreateVisuals();
        }
        for (let i = 0; i < this._gsoChildren.length; i++) {
            this._gsoChildren[i]._gsoCreateVisuals();
        }
    }

    _gsoUpdateVisuals() {
        if (!this._enabled || this.culled) return;
        if (this._hasVisuals) {
            if (this._requestedRecreateVisuals) {
                this._gsoDestroyVisuals();
                this._gsoCreateVisuals();
                this._requestedRecreateVisuals = false;
            }
            this.onUpdateVisuals();
        }
        for (let i = 0; i < this._gsoChildren.length; i++) {
            this._gsoChildren[i]._gsoUpdateVisuals();
        }
        if (this._hasVisuals) {
            this.onLateUpdateVisuals();
        }
    }

    _gsoDestroyVisuals() {
        for (let i = 0; i < this._gsoChildren.length; i++) {
            this._gsoChildren[i]._gsoDestroyVisuals();
        }
        if (this._hasVisuals) {
            this.onDestroyVisuals();
            this._hasVisuals = false;
        }
    }

    _gsoCreateBody(physicsWorld) {
        if (!this._enabled) return;
        if (!this._hasBody) {
            this._hasBody = true;
            this.onCreateBody(physicsWorld);
        }
        for (let i = 0; i < this._gsoChildren.length; i++) {
            this._gsoChildren[i]._gsoCreateBody(physicsWorld);
        }
    }

    _gsoDestroyBody(physicsWorld) {
        if (!physicsWorld) {
            try {
                physicsWorld = this.getGame().physicsWorld;
            } catch {
                physicsWorld = null;
            }
        }
        for (let i = 0; i < this._gsoChildren.length; i++) {
            this._gsoChildren[i]._gsoDestroyBody(physicsWorld);
        }
        if (this._hasBody) {
            this.onDestroyBody(physicsWorld);
            this._hasBody = false;
        }
    }

    _gsoUpdate(dt) {
        if (!this._enabled) return;
        this.onUpdate(dt);
        for (let i = 0; i < this._gsoChildren.length; i++) {
            this._gsoChildren[i]._gsoUpdate(dt);
        }
    }

    _gsoFixedUpdate(dt) {
        if (!this._enabled) return;
        this.onFixedUpdate(dt);
        for (let i = 0; i < this._gsoChildren.length; i++) {
            this._gsoChildren[i]._gsoFixedUpdate(dt);
        }
    }

    _gsoSetPlayMode(enable) {
        this._inPlayMode = enable;
        for (let i = 0; i < this._gsoChildren.length; i++) {
            this._gsoChildren[i]._gsoSetPlayMode(enable);
        }
    }

    // Get the pixicontainer for this parent.
    getParentPixiContainer() {
        if (this._gsoParent) {
            try {
                return this._gsoParent.getPixiContainer();
            } catch (err) {
                return this._gsoParent.getParentPixiContainer();
            }
        }
        throw new Error("Failed to retrieve parent pixi container. This GSObject doesn't have a GSO parent.");
    }

    getGame() {
        if (this.isGameClass) {
            return this;
        }
        if (this._gsoParent) {
            return this._gsoParent.getGame();
        }
        throw new Error('Parent of type Game could not be found. GSO structure expects a Game class at the root in order to work properly.');
    }

    requestNewVisuals() {
        if (this._hasVisuals) this._requestedRecreateVisuals = true;
    }

    // Enable this GSObject. Create visuals and body have to be called manually if required. Non recursive.
    enable() {
        this._enabled = true;
        if (this._gsoParent) this._gsoSetPlayMode(this._gsoParent._inPlayMode);
    }

    // Disable this GSObject and destroy the body and visuals if they exist.
    disable() {
        this._enabled = false;
        this._gsoDestroyBody();
        this._gsoDestroyVisuals();
        this._gsoSetPlayMode(false);
    }

    // Destroy this GSObject and its children.
    destroy() {
        this._destroyImplementation();
    }

    _destroyImplementation() {
        if (this._gsoParent) {
            const i = this._gsoParent._gsoChildren.indexOf(this);
            if (i >= 0) this._gsoParent._gsoChildren.splice(i, 1);
        }
        for (let i = 0; i < this._gsoChildren.length; i++) {
            this._gsoChildren[i].destroy();
        }
        this.onDestroy();
        this.disable();
    }

    _getRelevantUI(canvasPosition, isClick) {
        let match = { zIndex: 0, item: null };
        if (!this._enabled) return match;
        if (this._respondsToUI) {
            if ((isClick && this._absorbClick) || !isClick) {
                if (this.checkUICollision(canvasPosition)) {
                    match.zIndex = this.zIndex;
                    match.item = this;
                }
            }
        }
        for (let i = 0; i < this._gsoChildren.length; i++) {
            const newMatch = this._gsoChildren[i]._getRelevantUI(canvasPosition, isClick);
            if (!match.item || (newMatch.item && newMatch.zIndex >= match.zIndex)) {
                match = newMatch;
            }
        }
        return match;
    }

    // Serializes using the static "serializeableFields" field.
    autoSerialize(checkUnique = true) {
        const data = {};

        for (const key in this.constructor.serializeableFields) {
            if (key in this) {
                if (this[key] != null && this[key] != undefined) {
                    if (this[key].serialize) {
                        data[key] = this[key].serialize();
                    } else if (this.constructor.serializeableFields[key] === Number) {
                        data[key] = Number(this[key]);
                    } else if (this.constructor.serializeableFields[key] === Boolean) {
                        data[key] = Boolean(this[key]);
                    } else if (this.constructor.serializeableFields[key] === String) {
                        data[key] = String(this[key]);
                    } else if (this.constructor.serializeableFields[key] === Angle) {
                        data[key] = parseFloat(Angle.toDegrees(this[key]).toFixed(3));
                    } else {
                        data[key] = JSON.stringify(this[key]);
                    }
                }
            }
        }

        let uniqueData = data;
        if (checkUnique) {
            const defaultObj = new this.constructor();
            const defaultSerialized = defaultObj.autoSerialize(false);

            uniqueData = {};

            for (const key in data) {
                if (key in defaultSerialized) {
                    if (JSON.stringify(defaultSerialized[key]) !== JSON.stringify(data[key])) {
                        uniqueData[key] = data[key];
                    }
                } else {
                    uniqueData[key] = data[key];
                }
            }
        }

        return uniqueData;
    }

    // Deserializes using the static "serializeableFields" field.
    autoDeserialize(data) {
        for (const key in data) {
            if (key in this.constructor.serializeableFields) {
                let type = this.constructor.serializeableFields[key];
                if (type === Number) {
                    this[key] = Number(data[key]);
                } else if (type === Boolean) {
                    this[key] = Boolean(data[key] === true || data[key] == 'true');
                } else if (type === String) {
                    this[key] = String(data[key]);
                } else if (type === Angle) {
                    this[key] = Angle.toRadians(data[key]);
                } else if (type.prototype instanceof GSObject) {
                    if (this[key]) this[key].destroy();
                    this[key] = new type(this);
                    if (this[key].deserialize) {
                        this[key].deserialize(data[key]);
                    }
                } else if (Array.isArray(type)) {
                    type = type[0];
                    if (type.prototype instanceof GSObject) {
                        if (this[key]) this[key].destroy();
                        if (data[key] && data[key][0] && data[key][0].uuid) this[key] = new GSOUUIDArray(this, type);
                        else this[key] = new GSObjectArray(this, type);
                        if (this[key].deserialize) {
                            this[key].deserialize(data[key]);
                        }
                    } else {
                        throw 'Serializeable array fields of non GSOArray type is not supported.';
                    }
                } else {
                    this[key] = new type();
                    if (this[key].deserialize) {
                        this[key].deserialize(data[key]);
                    } else {
                        this[key] = JSON.parse(data[key]);
                    }
                }
            }
        }
    }

    // Returns a string displaying the tree structure of this object and its GSO children.
    showTree(level = '', last = true) {
        let prefix = '─';
        let prefixb = '├─';
        if (last) {
            prefixb = '└─';
        }
        if (this._gsoChildren.length > 0) prefix = '┬';
        let str = `${level + prefixb + prefix} ${this.constructor.name}\n`;
        let levelN = level;
        if (last) {
            levelN += '  ';
        } else {
            levelN += '| ';
        }
        for (let i = 0; i < this._gsoChildren.length; i++) {
            str += this._gsoChildren[i].showTree(levelN, i === this._gsoChildren.length - 1);
        }
        return str;
    }

    // ////==============//////
    // @@// OVERRIDEABLE //@@//
    // ////==============//////

    // Get pixi container.
    getPixiContainer() {
        if (this.pixiContainer) return this.pixiContainer;
        throw new Error("This GSObject doesn't have a pixi container, or doesn't have the getter setup.");
    }

    // Makes this object have visual presence in the world.
    onCreateVisuals() {}

    // Update the visual presence in the world.
    onUpdateVisuals() {}

    onLateUpdateVisuals() {}

    // Removes this object's visual presence from the world.
    onDestroyVisuals() {}

    // Makes this object have visual presence in the world.
    onCreateBody(physicsWorld) {}

    // Removes this object's visual presence from the world.
    onDestroyBody(physicsWorld) {}

    // Updated every tick.
    onUpdate(deltaTime) {}

    // Updated at a fixed rate (together with physics).
    onFixedUpdate() {}

    // Called before this object is destroyed.
    onDestroy() {}

    // Serialize this object and returns a JSON object.
    serialize() {
        return this.autoSerialize();
    }

    // Deserialize the given JSON object.
    deserialize(json) {
        this.autoDeserialize(json);
        this.requestNewVisuals();
    }
}

GSObject.serializeableFields = {};
